import Vue from 'vue';

let youtubeApiPromise = null;

export const loadYouTubeIframeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        if (typeof previousCallback === 'function') previousCallback();
      } catch (error) {
        // El reproductor de esta pantalla no depende de otros controladores.
      }
      resolve(window.YT);
    };

    let script = document.querySelector('script[data-youtube-iframe-api]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.dataset.youtubeIframeApi = 'true';
      script.onerror = () => {
        window.onYouTubeIframeAPIReady = previousCallback;
        if (script.parentNode) script.parentNode.removeChild(script);
        reject(new Error('No se pudo cargar el reproductor de video.'));
      };
      document.head.appendChild(script);
    }
  }).catch((error) => {
    youtubeApiPromise = null;
    throw error;
  });

  return youtubeApiPromise;
};

const getPublicationId = (publication) => (
  publication && (publication._id || publication.id)
);

export const trackMemberPublicationActivity = (publication, eventType = 'PUBLICATION_VIEW') => {
  const publicationId = getPublicationId(publication);
  if (!publicationId) return Promise.resolve();

  const recordActivity = () => Vue.axios.post(
    '/member-analytics/publication-activity',
    { publicationId, eventType },
  );

  return recordActivity().catch((error) => {
    const status = error && error.response && error.response.status;
    if (status && status < 500) return undefined;
    return recordActivity().catch(() => undefined);
  });
};

export const trackMemberDocumentConsult = (publication) =>
  trackMemberPublicationActivity(publication, 'PUBLICATION_VIEW');

const fileNameFromResponse = (response, publication) => {
  const disposition = response.headers && response.headers['content-disposition'];
  const encodedName = disposition && disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedName && encodedName[1]) {
    try {
      return decodeURIComponent(encodedName[1]);
    } catch (error) {
      // Use the document title when a server-provided name cannot be decoded.
    }
  }

  const plainName = disposition && disposition.match(/filename="?([^";]+)"?/i);
  if (plainName && plainName[1]) return plainName[1];

  const title = String((publication && (publication.name || publication.title)) || 'documento')
    .replace(/[\\/:*?"<>|\x00-\x1F\x7F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  let extension = '';
  const fileUrl = publication && (publication.url || publication.value);
  if (fileUrl) {
    try {
      const filePath = new URL(fileUrl, window.location.origin).pathname;
      const fileName = filePath.split('/').pop();
      const dot = fileName.lastIndexOf('.');
      extension = dot >= 0 ? fileName.slice(dot + 1) : '';
    } catch (error) {
      extension = '';
    }
  }
  const safeExtension = /^[a-z0-9]{1,8}$/i.test(extension) ? extension : 'pdf';
  return `${title || 'documento'}.${safeExtension}`;
};

export const downloadMemberDocument = async (publication) => {
  const publicationId = getPublicationId(publication);
  if (!publicationId) throw new Error('No pudimos identificar el documento.');

  const response = await Vue.axios({
    method: 'GET',
    url: `/publications/member/${encodeURIComponent(publicationId)}/download`,
    responseType: 'blob',
  });
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], {
      type: (response.headers && response.headers['content-type']) || 'application/octet-stream',
    });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileNameFromResponse(response, publication);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};
